import React, { useState, useEffect, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import {
    Typography, Card, CircularProgress, Box, MenuItem, Select, FormControl, InputLabel, alpha,
    Fade,
    Chip, useTheme
} from '@mui/material';
import { useSelector } from 'react-redux';
import { userSelector } from "../../user-redux/selector";
import dayjs from 'dayjs';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CHART_HEIGHT = 470;
const PX_PER_RECRUITER = 85;
const SCROLL_CHART_HEIGHT = 400; // bars + x labels; scrollbar sits flush under names
const LEGEND_HEIGHT = 36;

const truncateName = (value) => {
    const name = value == null ? '' : String(value);
    return name.length > 10 ? `${name.substring(0, 10)}...` : name;
};

const IndividualChart = ({ selectedCandidate, selectedSubmission, adminId, }) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('overall');
    const [selectedRecruiter, setSelectedRecruiter] = useState('');
    const [clampWidth, setClampWidth] = useState(0);
    const clampRef = useRef(null);
    const { user } = useSelector(userSelector);
    const theme = useTheme();

    // Measure the clamped (half-column) width — not the growing chart content
    useEffect(() => {
        if (loading) return undefined;
        const el = clampRef.current;
        if (!el) return undefined;

        const measure = () => setClampWidth(el.clientWidth || 0);
        measure();

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(measure);
            ro.observe(el);
            return () => ro.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [loading, chartData.length]);

    const chartPixelWidth = Math.max(
        clampWidth || 0,
        chartData.length * PX_PER_RECRUITER
    );

    // Single clean tooltip — avoids Recharts default double-render overlap
    const ChartTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;

        const uniqueRows = [];
        const seen = new Set();
        payload.forEach((entry) => {
            const key = entry.dataKey || entry.name;
            if (seen.has(key)) return;
            seen.add(key);
            uniqueRows.push(entry);
        });

        return (
            <Box
                sx={{
                    backgroundColor: '#fff',
                    border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                    minWidth: 160,
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                    {label}
                </Typography>
                {uniqueRows.map((entry) => (
                    <Typography
                        key={entry.dataKey || entry.name}
                        variant="body2"
                        sx={{ color: entry.color || theme.palette.text.primary, lineHeight: 1.6 }}
                    >
                        {`${entry.name || entry.dataKey} : ${entry.value ?? 0}`}
                    </Typography>
                ))}
            </Box>
        );
    };

    // Enhanced icon wrapper component for rich visual effects
    const RichIcon = ({
        children,
        color = theme.palette.primary.main,
        size = 'medium',
        variant = 'gradient' // 'gradient', 'glow', 'shadow', 'minimal'
    }) => {
        const sizeMap = {
            small: { p: 0.75, fontSize: '18px', borderRadius: 1.5 },
            medium: { p: 1, fontSize: '20px', borderRadius: 2 },
            large: { p: 1.5, fontSize: '24px', borderRadius: 2.5 }
        };

        const currentSize = sizeMap[size];

        const variantStyles = {
            gradient: {
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                color: '#FFF',
                boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 16px ${alpha(color, 0.4)}`,
                }
            },
            glow: {
                backgroundColor: alpha(color, 0.15),
                color: color,
                // border: `2px solid ${alpha(color, 0.3)}`,
                boxShadow: `0 0 20px ${alpha(color, 0.2)}, inset 0 1px 0 ${alpha('#FFF', 0.2)}`,
                '&:hover': {
                    backgroundColor: alpha(color, 0.25),
                    boxShadow: `0 0 25px ${alpha(color, 0.3)}, inset 0 1px 0 ${alpha('#FFF', 0.3)}`,
                }
            },
            shadow: {
                backgroundColor: theme.palette.background.paper,
                color: color,
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                border: `1px solid ${alpha(color, 0.2)}`,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
                }
            },
            minimal: {
                backgroundColor: alpha(color, 0.1),
                color: color,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(color, 0.2)}`,
            }
        };

        return (
            <Box
                sx={{
                    p: currentSize.p,
                    borderRadius: currentSize.borderRadius,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': variant === 'gradient' ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: `linear-gradient(180deg, ${alpha('#FFF', 0.2)} 0%, transparent 100%)`,
                        pointerEvents: 'none',
                    } : {},
                    ...variantStyles[variant]
                }}
            >
                {React.cloneElement(children, {
                    sx: { fontSize: currentSize.fontSize, ...children.props.sx }
                })}
            </Box>
        );
    };

    // Enhanced chip component with rich icons
    const RichChip = ({ icon, label, color, count }) => (
        <Chip
            icon={
                <RichIcon color={color} size="small" variant="glow">
                    {icon}
                </RichIcon>
            }
            label={
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={600}>
                        {count}
                    </Typography>
                    <Typography variant="body2">
                        {label}
                    </Typography>
                </Box>
            }
            variant="outlined"
            sx={{
                backgroundColor: alpha(color, 0.08),
                borderColor: alpha(color, 0.25),
                color: color,
                fontWeight: 600,
                height: 40,
                borderRadius: 3,
                '& .MuiChip-label': {
                    px: 2,
                },
                '&:hover': {
                    backgroundColor: alpha(color, 0.12),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
                }
            }}
        />
    );

    // Calculate summary statistics
    const summaryStats = React.useMemo(() => {
        if (!chartData.length) return { totalSubmissions: 0, totalSelected: 0, activeRecruiters: 0, totalInterviews: 0 };

        const totalSubmissions = chartData.reduce((sum, item) => sum + (item.submissionsCount || 0), 0);
        const totalSelected = chartData.reduce((sum, item) => sum + (item.selectedCount || 0), 0);
        const totalInterviews = chartData.reduce((sum, item) => sum + (item.interviewsCount || 0), 0);
        const activeRecruiters = chartData.length;

        return { totalSubmissions, totalSelected, totalInterviews, activeRecruiters };
    }, [chartData]);

    const handleRecruiterChange = (event) => {
        setSelectedRecruiter(event.target.value);
    };

    // Update fetchData to filter by selected recruiter
    const fetchData = async () => {
        setLoading(true);

        const now = dayjs();
        let fromDate = '', toDate = '';

        if (viewMode === 'overall') {
            // For overall, we don't need to set dates
            fromDate = '';
            toDate = '';
        } else if (viewMode === 'daily') {
            fromDate = now.format('YYYY-MM-DD');
            toDate = now.format('YYYY-MM-DD');
        } else if (viewMode === 'weekly') {
            fromDate = now.startOf('week').format('YYYY-MM-DD');
            toDate = now.endOf('week').format('YYYY-MM-DD');
        } else if (viewMode === 'monthly') {
            fromDate = now.startOf('month').format('YYYY-MM-DD');
            toDate = now.endOf('month').format('YYYY-MM-DD');
        }

        try {
            const response = await fetch(
                `https://bsapi.jobsnprofiles.com/candidate/recruiters-submissions-count`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        admin_id: user?.ca_id || adminId || user?.companyid,
                        interval: viewMode, // 'overall', 'daily', 'weekly', 'monthly'
                        recruiter_id: selectedRecruiter || undefined, // optional
                        fromDate: fromDate,
                    }),
                }
            );

            const data = await response.json();
            if (data?.data?.recruiters) {
                const activeRecruiters = data.data.recruiters.filter(
                    (recruiter) => recruiter.activation === '1'
                );
                setChartData(
                    selectedRecruiter
                        ? activeRecruiters.filter((rec) => rec.id === selectedRecruiter)
                        : activeRecruiters
                );
            } else {
                setChartData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [adminId, user?.id, viewMode, selectedRecruiter]);

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                height: '100%',
                overflow: 'hidden',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    borderRadius: '12px 12px 0 0',
                }}
            >
                <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1.5}
                    mb={2}
                >
                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <RichIcon
                            color={theme.palette.primary.main}
                            size="medium"
                            variant="gradient"
                        >
                            <BarChartIcon />
                        </RichIcon>
                        <Box minWidth={0}>
                            <Typography variant="h6" fontWeight={700} color="text.primary" noWrap>
                                Submission Analysis
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                Track recruiter performance, submission
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" flexWrap="wrap" gap={1.5}>
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: 120,
                                height: 40,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                    fontSize: '0.875rem', // Decrease font size
                                    height: '40px', // Ensure equal height
                                    '&:hover': {
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem', // Decrease font size for label
                                },
                            }}
                        >
                            <InputLabel id="view-mode-label">Filter</InputLabel>
                            <Select
                                labelId="view-mode-label"
                                value={viewMode}
                                label="Filter"
                                onChange={(e) => setViewMode(e.target.value)}
                            >
                                <MenuItem value="overall">
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <RichIcon
                                            color={theme.palette.warning.main}
                                            size="small"
                                            variant="minimal"
                                        >
                                            <TrendingUpIcon />
                                        </RichIcon>
                                        <Typography variant="body2">Overall View</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="daily">
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <RichIcon
                                            color={theme.palette.primary.main}
                                            size="small"
                                            variant="minimal"
                                        >
                                            <TodayIcon />
                                        </RichIcon>
                                        <Typography variant="body2">Daily View</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="weekly">
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <RichIcon
                                            color={theme.palette.secondary.main}
                                            size="small"
                                            variant="minimal"
                                        >
                                            <DateRangeIcon />
                                        </RichIcon>
                                        <Typography variant="body2">Weekly View</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="monthly">
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <RichIcon
                                            color={theme.palette.success.main}
                                            size="small"
                                            variant="minimal"
                                        >
                                            <CalendarMonthIcon />
                                        </RichIcon>
                                        <Typography variant="body2">Monthly View</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl
                            size="small"
                            sx={{
                                minWidth: 120,
                                height: 40,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                    fontSize: '0.875rem',
                                    height: '40px',
                                    '&:hover': {
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem',
                                },
                            }}
                        >
                            <InputLabel id="recruiter-select-label">Recruiter</InputLabel>
                            <Select
                                labelId="recruiter-select-label"
                                value={selectedRecruiter}
                                label="Recruiter"
                                onChange={handleRecruiterChange}
                            >
                                <MenuItem value="">
                                    <Typography variant="body2">All Recruiters</Typography>
                                </MenuItem>
                                {chartData.map((recruiter) => (
                                    <MenuItem key={recruiter.id} value={recruiter.id}>
                                        <Typography variant="body2">{recruiter.name}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Enhanced Summary Stats */}
                {!loading && chartData.length > 0 && (
                    <Fade in={!loading}>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <RichChip
                                icon={<TrendingUpIcon />}
                                label="Total Submissions"
                                color={theme.palette.success.main}
                                count={summaryStats.totalSubmissions}
                            />
                            <RichChip
                                icon={<CheckCircleIcon />}
                                label="Total Interviews"
                                color={theme.palette.info.main}
                                count={summaryStats.totalInterviews}
                            />
                            <RichChip
                                icon={<CheckCircleIcon />}
                                label="Selected"
                                color={theme.palette.success.main}
                                count={summaryStats.totalSelected}
                            />
                            {/* {!selectedRecruiter && (
                                <RichChip
                                    icon={<PeopleIcon />}
                                    label="Active Recruiters"
                                    color={theme.palette.warning.main}
                                    count={summaryStats.activeRecruiters}
                                />
                            )} */}
                        </Box>
                    </Fade>
                )}
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={CHART_HEIGHT}>
                    <CircularProgress size={48} thickness={4} sx={{ color: theme.palette.primary.main }} />
                </Box>
            ) : chartData.length > 0 ? (
                <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                    {/* Legend above chart so scrollbar can sit right under X-axis names */}
                    <Box
                        sx={{
                            height: LEGEND_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            px: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            { key: 'submissionsCount', color: '#864242ff', label: 'submissionsCount' },
                            { key: 'selectedCount', color: '#82ca9d', label: 'selectedCount' },
                            { key: 'interviewsCount', color: '#4fca8cff', label: 'interviewsCount' },
                        ].map((item) => (
                            <Box key={item.key} display="flex" alignItems="center" gap={0.75}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: item.color }} />
                                <Typography variant="caption" color="text.secondary">
                                    {item.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Scroll area ends at X-axis names so scrollbar is right under them */}
                    <Box
                        ref={clampRef}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            boxSizing: 'border-box',
                            lineHeight: 0,
                            pb: 0,
                            mb: 0,
                            scrollbarGutter: 'stable',
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${theme.palette.primary.main} ${alpha(theme.palette.primary.main, 0.15)}`,
                            '&::-webkit-scrollbar': {
                                height: 8,
                                display: 'block',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: 4,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: chartPixelWidth || '100%',
                                height: SCROLL_CHART_HEIGHT,
                                lineHeight: 0,
                            }}
                        >
                            <BarChart
                                width={chartPixelWidth || 400}
                                height={SCROLL_CHART_HEIGHT}
                                data={chartData}
                                margin={{ top: 8, right: 16, left: 4, bottom: 0 }}
                                barCategoryGap={selectedRecruiter ? '45%' : '28%'}
                                barGap={2}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    tick={{
                                        fontSize: 11,
                                        fill: theme.palette.text.primary,
                                        fontWeight: 500,
                                    }}
                                    tickLine={{ stroke: theme.palette.divider }}
                                    height={36}
                                    tickMargin={4}
                                    tickFormatter={truncateName}
                                />
                                <YAxis width={40} tick={{ fontSize: 11 }} />
                                <Tooltip
                                    content={<ChartTooltip />}
                                    cursor={{ fill: alpha(theme.palette.primary.main, 0.06) }}
                                    isAnimationActive={false}
                                    wrapperStyle={{ outline: 'none', zIndex: 10 }}
                                />
                                <Bar
                                    dataKey="submissionsCount"
                                    name="submissionsCount"
                                    stackId="a"
                                    fill="#864242ff"
                                    maxBarSize={32}
                                    barSize={selectedRecruiter ? 22 : 28}
                                    isAnimationActive={false}
                                />
                                <Bar
                                    dataKey="selectedCount"
                                    name="selectedCount"
                                    stackId="a"
                                    fill="#82ca9d"
                                    maxBarSize={32}
                                    barSize={selectedRecruiter ? 22 : 28}
                                    isAnimationActive={false}
                                />
                                <Bar
                                    dataKey="interviewsCount"
                                    name="interviewsCount"
                                    stackId="a"
                                    fill="#4fca8cff"
                                    maxBarSize={32}
                                    barSize={selectedRecruiter ? 22 : 28}
                                    isAnimationActive={false}
                                />
                            </BarChart>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height={CHART_HEIGHT}
                    gap={2}
                >
                    <RichIcon
                        color={theme.palette.text.disabled}
                        size="large"
                        variant="shadow"
                    >
                        <InfoOutlinedIcon />
                    </RichIcon>
                    <Typography variant="body2" color="textSecondary">
                        No data available for selected range.
                    </Typography>
                </Box>
            )}
        </Card>
    );
};

export default IndividualChart;